/**
 * CoClosingAgentSelector Component
 * Allows selecting up to 3 co-closing agents using a Leader -> Agent cascading flow
 * Used in the Sales Form to add agents who collaborated on closing the sale
 */

import React, { useState } from 'react';

// Helper to get nickname (fallback to fullName)
const getNickname = (user) => {
  if (!user) return 'Unknown';
  return user.nickname || user.fullName || 'Unknown';
};

function CoClosingAgentSelector({
  coClosingAgents = [],       // Array of { agentId, salesLeaderId, agent?, leader? }
  primaryCloserId,            // UUID of the designated primary closer
  submittingAgentId,          // UUID of the agent submitting the form
  submittingAgentName,        // Name of the agent submitting (for display)
  leadersWithAgents = [],     // Array of leaders with their teamMembers
  onChange,                   // (coClosingAgents) => void
  onPrimaryChange,            // (primaryCloserId) => void
  maxAgents = 3,
  disabled = false
}) {
  const [showModal, setShowModal] = useState(false);
  const [selectedLeaderId, setSelectedLeaderId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');

  // Get agents for the currently selected leader
  const selectedLeader = leadersWithAgents.find(l => l.id === selectedLeaderId);
  const availableAgents = selectedLeader?.teamMembers || [];

  // Filter out agents that are already selected or are the submitting agent
  const alreadySelectedIds = [submittingAgentId, ...coClosingAgents.map(a => a.agentId)];
  const filteredAgents = availableAgents.filter(a => !alreadySelectedIds.includes(a.id));

  const handleAddAgent = () => {
    if (!selectedAgentId || !selectedLeaderId) return;

    const agent = availableAgents.find(a => a.id === selectedAgentId);
    const leader = leadersWithAgents.find(l => l.id === selectedLeaderId);

    const newAgent = {
      agentId: selectedAgentId,
      salesLeaderId: selectedLeaderId,
      agent: agent ? { id: agent.id, nickname: agent.nickname, fullName: agent.fullName } : null,
      leader: leader ? { id: leader.id, nickname: leader.nickname, fullName: leader.fullName } : null
    };

    onChange([...coClosingAgents, newAgent]);
    setShowModal(false);
    setSelectedLeaderId('');
    setSelectedAgentId('');
  };

  const handleRemoveAgent = (agentId) => {
    const updated = coClosingAgents.filter(a => a.agentId !== agentId);
    onChange(updated);

    // If the removed agent was the primary, reset to submitter
    if (primaryCloserId === agentId) {
      onPrimaryChange(submittingAgentId);
    }
  };

  const handlePrimaryChange = (agentId) => {
    onPrimaryChange(agentId);
  };

  const canAddMore = coClosingAgents.length < maxAgents && !disabled;

  return (
    <div className="space-y-4">
      {/* Primary Closer Selection */}
      <div className="bg-gray-50 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Who is the Primary Closer?
        </label>
        <div className="space-y-2">
          {/* Submitting agent option */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="primaryCloser"
              checked={!primaryCloserId || primaryCloserId === submittingAgentId}
              onChange={() => handlePrimaryChange(submittingAgentId)}
              disabled={disabled}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-900">
              {submittingAgentName || 'Me'} (Submitter)
            </span>
          </label>

          {/* Co-closing agents options */}
          {coClosingAgents.map(coAgent => (
            <label key={coAgent.agentId} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="primaryCloser"
                checked={primaryCloserId === coAgent.agentId}
                onChange={() => handlePrimaryChange(coAgent.agentId)}
                disabled={disabled}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-900">
                {getNickname(coAgent.agent)}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Selected Co-Closing Agents List */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Co-Closing Agents ({coClosingAgents.length}/{maxAgents})
        </label>

        {coClosingAgents.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No co-closing agents added yet</p>
        ) : (
          <div className="space-y-2">
            {coClosingAgents.map(coAgent => (
              <div
                key={coAgent.agentId}
                className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {getNickname(coAgent.agent)}
                      {primaryCloserId === coAgent.agentId && (
                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                          Primary
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      Team: {getNickname(coAgent.leader)}
                    </p>
                  </div>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAgent(coAgent.agentId)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add Button */}
        {canAddMore && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="mt-3 flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Co-Closer
          </button>
        )}
      </div>

      {/* Add Agent Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Co-Closing Agent</h3>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setSelectedLeaderId('');
                  setSelectedAgentId('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Step 1: Select Leader */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  1. Select Team Leader
                </label>
                <select
                  value={selectedLeaderId}
                  onChange={(e) => {
                    setSelectedLeaderId(e.target.value);
                    setSelectedAgentId(''); // Reset agent when leader changes
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- Select a Leader --</option>
                  {leadersWithAgents.map(leader => (
                    <option key={leader.id} value={leader.id}>
                      {getNickname(leader)} ({leader.teamMembers?.length || 0} agents)
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 2: Select Agent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  2. Select Agent
                </label>
                <select
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  disabled={!selectedLeaderId}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">-- Select an Agent --</option>
                  {filteredAgents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {getNickname(agent)}
                    </option>
                  ))}
                  {selectedLeaderId && filteredAgents.length === 0 && (
                    <option disabled>No available agents in this team</option>
                  )}
                </select>
                {selectedLeaderId && filteredAgents.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    All agents in this team are already selected or unavailable.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setSelectedLeaderId('');
                  setSelectedAgentId('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddAgent}
                disabled={!selectedAgentId}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Add Agent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoClosingAgentSelector;
